import { MenuItemProps } from '@lsy-netline/netline-ui/MenuItem';
import clsx from 'clsx';
import { MouseEvent, MouseEventHandler, forwardRef, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as flags from 'flags';
import { useLanguage } from 'hooks';
import { ArrowRightIcon } from 'icons';
import { Menu, MenuItem, Typography, styled, useTheme } from 'netline-ui';
import services from 'services';
import { CloseableComponentProps } from 'types';
import getAppFrameTheme from 'utils/theme';

import {
  languageSelectorMenuClasses,
  languageSelectorMenuItemClasses,
  languageSelectorMenuItemStyles,
  languageSelectorMenuMenuItemClasses,
  languageSelectorMenuStyles,
} from './LanguageSelector.styles';

const LanguageSelectorMenuItemRoot = styled(MenuItem, { name: languageSelectorMenuItemClasses.$name })(
  languageSelectorMenuItemStyles,
);
const LanguageSelectorMenuRoot = styled(Menu, { name: languageSelectorMenuClasses.$name })(languageSelectorMenuStyles);

type LanguageSelectorProps = CloseableComponentProps & MenuItemProps;

const LanguageSelector = forwardRef<HTMLLIElement, LanguageSelectorProps>(
  ({ className, onMenuClose, ...props }, ref) => {
    const { availableLanguages, selectedLanguage } = useLanguage();
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const {
      appHeader: {
        preferencesMenu: {
          languageSelector: { hideFlags },
        },
      },
    } = getAppFrameTheme(useTheme());

    const handleToggleMenuClick = useCallback((event) => setAnchorEl(event.currentTarget), []);
    const handleMenuClose = useCallback(() => setAnchorEl(null), []);

    const handleSelectLanguage: MouseEventHandler<HTMLLIElement> = (event) => {
      const language = event.currentTarget.dataset.value;
      if (!language || language === selectedLanguage) {
        return;
      }
      services.appFrame.language.setLanguage(language);
      handleMenuClose();
    };

    const handleMouseEnter = useCallback((event) => {
      setAnchorEl(event.currentTarget);
    }, []);

    const submenuRef = useRef<HTMLDivElement | null>(null);
    const handleMouseLeave = useCallback((event: MouseEvent<HTMLElement>) => {
      const { relatedTarget } = event;
      // `realtedTarget` can be eg. a `window` object as well which not works together with `contains(...)` method
      if (relatedTarget instanceof Node && submenuRef.current?.contains(relatedTarget)) {
        return;
      }
      setAnchorEl(null);
    }, []);

    return (
      <>
        <LanguageSelectorMenuItemRoot
          ref={ref}
          className={clsx(className, languageSelectorMenuItemClasses.root)}
          data-testid="LanguageSelector-root"
          onClick={handleToggleMenuClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...props}
        >
          <div>{t('languages.language')}</div>
          <ArrowRightIcon data-testid="LanguageSelector-menu" className={languageSelectorMenuItemClasses.arrow} />
        </LanguageSelectorMenuItemRoot>
        <LanguageSelectorMenuRoot
          ref={submenuRef}
          className={languageSelectorMenuClasses.root}
          classes={{ paper: languageSelectorMenuClasses.paper }}
          keepMounted
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableAutoFocusItem
          hideBackdrop
          onMouseLeave={handleMouseLeave}
        >
          {availableLanguages?.map((language) => (
            <MenuItem
              onClick={handleSelectLanguage}
              key={language}
              value={language}
              className={clsx(languageSelectorMenuClasses.menuItem, {
                [languageSelectorMenuMenuItemClasses.selected]: language === selectedLanguage,
              })}
              data-value={language}
              data-testid={`LanguageSelector-item-${language}`}
            >
              {!hideFlags && (
                <img src={flags[language]} alt={language} className={languageSelectorMenuClasses.menuItemFlag} />
              )}
              <Typography>{t(`languages.${language}`)}</Typography>
            </MenuItem>
          ))}
        </LanguageSelectorMenuRoot>
      </>
    );
  },
);

export default LanguageSelector;
